import { yupResolver } from '@hookform/resolvers/yup';
import { CurrencyDollar } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { classOptions } from 'src/app/Orders/order.const';
import InputText from 'src/components/InputText/InputText';
import SelectBox from 'src/components/SelectBox/SelectBox';
import Toggle from 'src/components/Toggle/Toggle';
import { CURRENCY, currencyOptions } from 'src/constants/common';
import { selectFormData, setQuoteBookingFormData } from 'src/redux/bookingStepper.slice';
import { RootState } from 'src/redux/store';
import { getPricingRate } from 'src/services/CommonService';
import { logError } from 'src/services/LoggingService';
import { formatDecimalNumber } from 'src/utils/CommonFunctions';
import * as Yup from 'yup';

import { IFormData, IOrderDetailsProps } from './OrderDetails.interface';

const parseToArray = (value: any): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter((item: string) => item?.trim() !== '');
    if (typeof value === 'string') return value.split('|').filter((item: string) => item.trim() !== '');

    return [];
};

const OrderDetails: React.FC<IOrderDetailsProps> = ({ stepperFooterRef }) => {
    const { t } = useTranslation();
    const bookingFormData = useSelector(selectFormData);
    const dispatch = useDispatch();
    const { isVehicleTypeLoading, vehicleTypeOptions, commodityOptions } = useSelector((state: RootState) => state.basic);

    const validationSchema = Yup.object().shape({
        orderType: Yup.string().required('Order type is required'),
        equipmentType: Yup.string().required(t('Orders.Order_Stepper.Order_Details.Equipment_type_required')),
        poNumber: Yup.string().nullable(),
        refNumber: Yup.string().nullable(),
        pickupNumber: Yup.string().nullable(),
        deliveryNumber: Yup.string().nullable(),
        isLoadInsuranceEnabled: Yup.boolean().default(false),
        insuranceCommodity: Yup.array()
            .of(Yup.string())
            .default([])
            .when('isLoadInsuranceEnabled', {
                is: true,
                then: (schema) => schema.min(1, 'Insurance commodity is required'),
                otherwise: (schema) => schema.notRequired(),
            }),
        insuranceCommercialValue: Yup.mixed().when('isLoadInsuranceEnabled', {
            is: true,
            then: () =>
                Yup.number()
                    .transform((value, originalValue) => (originalValue === '' ? null : value))
                    .nullable()
                    .required('Insurance commercial value is required')
                    .min(0.01, 'Insurance commercial value must be a positive number')
                    .typeError('Insurance commercial value must be a valid number'),
            otherwise: () => Yup.mixed().nullable().notRequired(),
        }),
        insuranceCurrencyType: Yup.string().default(CURRENCY.CAD),
    }) as Yup.ObjectSchema<IFormData>;

    const { control, clearErrors, watch, trigger, setValue, getValues } = useForm<IFormData>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onSubmit',
        defaultValues: {
            orderType: bookingFormData?.orderDetails?.orderType || 'ltl',
            equipmentType: bookingFormData?.orderDetails?.equipmentType || 'V',
            poNumber: bookingFormData?.orderDetails?.poNumber || null,
            refNumber: bookingFormData?.orderDetails?.refNumber || null,
            pickupNumber: bookingFormData?.orderDetails?.pickupNumber || null,
            deliveryNumber: bookingFormData?.orderDetails?.deliveryNumber || null,
            isLoadInsuranceEnabled: bookingFormData?.orderDetails?.isLoadInsuranceEnabled ?? false,
            insuranceCommodity: parseToArray(bookingFormData?.orderDetails?.insuranceCommodity),
            insuranceCommercialValue: bookingFormData?.orderDetails?.insuranceCommercialValue || '',
            insuranceCurrencyType: bookingFormData?.orderDetails?.insuranceCurrencyType || CURRENCY.CAD,
        },
    });

    const isLoadInsuranceEnabled = watch('isLoadInsuranceEnabled');
    const insuranceCommercialValue = watch('insuranceCommercialValue');
    const insuranceCurrencyType = watch('insuranceCurrencyType');

    // Tracks whether manual validation has been triggered (replaces isSubmitted
    // which is never set since we call trigger() directly, not handleSubmit).
    const hasBeenValidated = useRef(false);

    const [commodityInputValue, setCommodityInputValue] = useState<string>('');
    const [filteredCommodityOptions, setFilteredCommodityOptions] = useState<any[]>([]);
    const [isCommodityMenuOpen, setIsCommodityMenuOpen] = useState<boolean>(false);
    const [commoditySelectKey, setCommoditySelectKey] = useState<number>(0);

    const [currencyRates, setCurrencyRates] = useState<{ cadRate: number; usdRate: number }>({
        cadRate: 1,
        usdRate: 0.75,
    });
    const prevInsuranceCurrencyRef = useRef<string | null>(null);
    const isConvertingCurrencyRef = useRef<boolean>(false);

    useEffect(() => {
        if (commodityOptions && commodityOptions.length > 0) {
            setFilteredCommodityOptions(commodityOptions);
        }
    }, [commodityOptions]);

    // Fetch live CAD/USD exchange rates on mount
    useEffect(() => {
        getPricingRate()
            .then((response: any) => {
                if (response?.data) {
                    setCurrencyRates({
                        cadRate: response.data.cadRate || 1,
                        usdRate: response.data.usdRate || 0.75,
                    });
                }
            })
            .catch(async (err: any) => {
                await logError(err, {
                    errorMessage: err?.message || 'Error while fetching pricing rates',
                    errorLocation: 'OrderDetails: getPricingRate',
                    storeInDatabase: true,
                    logToConsole: true,
                });
            });
    }, []);

    const convertCurrency = useCallback(
        (value: number, fromCurrency: string, toCurrency: string): number => {
            if (!value || value === 0 || fromCurrency === toCurrency) {
                return formatDecimalNumber(value || 0, 2);
            }

            let convertedValue = 0;

            if (fromCurrency === CURRENCY.CAD && toCurrency === CURRENCY.USD) {
                convertedValue = value * (currencyRates.usdRate || 0.75);
            } else if (fromCurrency === CURRENCY.USD && toCurrency === CURRENCY.CAD) {
                convertedValue = value * (currencyRates.cadRate || 1);
            } else {
                convertedValue = value;
            }

            return formatDecimalNumber(convertedValue, 2);
        },
        [currencyRates]
    );

    // Capture the initial insurance currency on first render
    useEffect(() => {
        if (prevInsuranceCurrencyRef.current === null && insuranceCurrencyType) {
            prevInsuranceCurrencyRef.current = insuranceCurrencyType;
        }
    }, [insuranceCurrencyType]);

    // Auto-convert commercial value when insurance currency changes
    useEffect(() => {
        if (prevInsuranceCurrencyRef.current && prevInsuranceCurrencyRef.current !== insuranceCurrencyType && insuranceCommercialValue && !isConvertingCurrencyRef.current) {
            isConvertingCurrencyRef.current = true;
            const numericValue = parseFloat(String(insuranceCommercialValue)) || 0;

            if (numericValue > 0) {
                const convertedValue = convertCurrency(numericValue, prevInsuranceCurrencyRef.current, insuranceCurrencyType);
                setValue('insuranceCommercialValue', String(convertedValue));

                if (hasBeenValidated.current) {
                    trigger('insuranceCommercialValue');
                }
            }

            setTimeout(() => {
                isConvertingCurrencyRef.current = false;
            }, 100);
        }
        prevInsuranceCurrencyRef.current = insuranceCurrencyType;
    }, [insuranceCurrencyType, insuranceCommercialValue, convertCurrency, setValue, trigger]);

    const onSubmit = useCallback(
        async (data: IFormData) => {
            const submitData: any = {
                orderDetails: {
                    ...bookingFormData?.orderDetails,
                    orderType: data.orderType,
                    equipmentType: data.equipmentType,
                    equipmentTypeFullName: vehicleTypeOptions?.find((e: any) => e?.value === data.equipmentType)?.label || '',
                    poNumber: data.poNumber,
                    refNumber: data.refNumber,
                    pickupNumber: data.pickupNumber,
                    deliveryNumber: data.deliveryNumber,
                    isLoadInsuranceEnabled: data.isLoadInsuranceEnabled,
                    insuranceCommodity: Array.isArray(data.insuranceCommodity) && data.insuranceCommodity.length > 0 ? data.insuranceCommodity.join('|') : null,
                    insuranceCommercialValue: data.insuranceCommercialValue || null,
                    insuranceCurrencyType: data.insuranceCurrencyType || null,
                },
            };

            dispatch(setQuoteBookingFormData(submitData));
        },
        [dispatch, bookingFormData?.orderDetails, vehicleTypeOptions]
    );

    const handleNext = useCallback(async () => {
        hasBeenValidated.current = true;
        const isValid = await trigger();

        if (!isValid) {
            return Promise.reject(new Error('Validation failed'));
        }

        const formValues = getValues();
        await onSubmit(formValues);
    }, [trigger, getValues, onSubmit]);

    const handleBack = useCallback(() => {
        const data = getValues();
        const submitData: any = {
            orderDetails: {
                ...bookingFormData?.orderDetails,
                orderType: data.orderType,
                equipmentType: data.equipmentType,
                poNumber: data.poNumber,
                refNumber: data.refNumber,
                pickupNumber: data.pickupNumber,
                deliveryNumber: data.deliveryNumber,
                isLoadInsuranceEnabled: data.isLoadInsuranceEnabled,
                insuranceCommodity: Array.isArray(data.insuranceCommodity) && data.insuranceCommodity.length > 0 ? data.insuranceCommodity.join('|') : null,
                insuranceCommercialValue: data.insuranceCommercialValue || null,
                insuranceCurrencyType: data.insuranceCurrencyType || null,
            },
        };

        dispatch(setQuoteBookingFormData(submitData));
    }, [dispatch, getValues, bookingFormData?.orderDetails]);

    const handleOrderTypeChange = (onChange: (value: string) => void) => (selectedOption: any) => {
        onChange(selectedOption?.value || null);
        clearErrors('orderType');
    };

    const handleEquipmentTypeChange = (onChange: (value: string) => void) => (selectedOption: any) => {
        onChange(selectedOption?.value || null);
        clearErrors('equipmentType');
    };

    const memorizedEquipmentTypeSelectBox = useMemo(
        () => (
            <Controller
                name="equipmentType"
                control={control}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <SelectBox
                        parentClassName="w-full"
                        labelClassName="form_label block mb-1.5"
                        name="equipmentType"
                        id="equipmentType"
                        className={`form_control`}
                        placeholder={t('Orders.Order_Stepper.Order_Details.Select_equipment_type_placeholder')}
                        isSearchable={true}
                        isClearable={false}
                        options={vehicleTypeOptions}
                        onChangeFunc={handleEquipmentTypeChange(onChange)}
                        value={value ? vehicleTypeOptions?.find((e: any) => e?.value === value) : null}
                        errorText={error?.message ? true : false}
                        isLoading={isVehicleTypeLoading}
                        isDisabled={true}
                    />
                )}
            />
        ),
        [control, isVehicleTypeLoading, vehicleTypeOptions, t]
    );

    const handleInsuranceCommodityMultipleChange = (onChange: any) => (selectedOptions: any) => {
        if (selectedOptions && Array.isArray(selectedOptions)) {
            onChange(selectedOptions.map((option: any) => option.value));
        } else {
            onChange([]);
        }

        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);

        if (hasBeenValidated.current) trigger('insuranceCommodity');
    };

    const handleCreateCommodityOption = (onChange: any) => (inputValue: string) => {
        const trimmedValue = inputValue?.trim();

        if (trimmedValue) {
            const currentValue = watch('insuranceCommodity') || [];
            const newValue = Array.isArray(currentValue) ? [...currentValue, trimmedValue] : [trimmedValue];
            onChange(newValue);
            setCommodityInputValue('');
            setFilteredCommodityOptions(commodityOptions);
            setCommoditySelectKey((prev) => prev + 1);

            if (hasBeenValidated.current) trigger('insuranceCommodity');
        }
    };

    const handleCommodityInputChange = (inputValue: string, actionMeta: any) => {
        if (actionMeta.action === 'input-change') {
            setCommodityInputValue(inputValue);

            if (!inputValue.trim()) {
                setFilteredCommodityOptions(commodityOptions);
            } else {
                const filtered = commodityOptions.filter((option: any) => {
                    const label = typeof option === 'string' ? option : option.label || '';
                    const value = typeof option === 'string' ? option : option.value || '';

                    return (
                        (label && typeof label === 'string' && label.toLowerCase().includes(inputValue.toLowerCase())) ||
                        (value && typeof value === 'string' && value.toLowerCase().includes(inputValue.toLowerCase()))
                    );
                });
                setFilteredCommodityOptions(filtered);
            }
        } else if (actionMeta.action === 'menu-close') {
            setCommodityInputValue('');
        }
    };

    const handleCommodityMenuOpen = () => {
        setIsCommodityMenuOpen(true);
        setFilteredCommodityOptions(commodityOptions);
        setCommodityInputValue('');
    };

    const handleCommodityMenuClose = () => {
        setIsCommodityMenuOpen(false);
        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);
    };

    const handleClearCommodities = (onChange: any) => () => {
        setIsCommodityMenuOpen(false);
        onChange([]);
        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);
    };

    const handleCommodityKeyDown = useCallback(
        (onChange: any, currentValue: string[]) => (event: React.KeyboardEvent) => {
            if (event.key === 'Backspace' && !commodityInputValue && Array.isArray(currentValue) && currentValue.length > 0) {
                event.preventDefault();
                const newValue = currentValue.slice(0, -1);
                onChange(newValue);

                if (hasBeenValidated.current) trigger('insuranceCommodity');
            }
        },
        [commodityInputValue, trigger]
    );

    const handleInsuranceCommercialValueChange = (onChange: any) => (e: any) => {
        onChange(e.target.value);

        if (hasBeenValidated.current) trigger('insuranceCommercialValue');
    };

    const handleInsuranceCurrencyChange = (onChange: any) => (selectedOption: any) => {
        onChange(selectedOption?.value || CURRENCY.CAD);
    };

    const handleLoadInsuranceToggle = (onChange: any) => (e: any) => {
        onChange(e.target.checked);

        if (!e.target.checked) {
            setValue('insuranceCommercialValue', '');
            clearErrors(['insuranceCommercialValue', 'insuranceCommodity']);
        }

        if (hasBeenValidated.current) {
            trigger('insuranceCommercialValue');
            trigger('insuranceCommodity');
        }
    };

    useEffect(() => {
        const stepperHandlers = {
            triggerNext: async () => {
                await handleNext();
            },
            triggerBack: async () => {
                await handleBack();
            },
            triggerValidation: async () => {
                hasBeenValidated.current = true;
                await trigger();
            },
        };

        if (stepperFooterRef && 'current' in stepperFooterRef) {
            (stepperFooterRef as any).current = stepperHandlers;
        }
    }, [stepperFooterRef, handleBack, handleNext, trigger]);

    return (
        <>
            <div className="animate-field-item">
                <div className="stepper-content-container">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common">
                            {t('Orders.Order_Type')}
                            <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                        </label>
                    </div>
                    <div className="flex-1">
                        <Controller
                            name="orderType"
                            control={control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <SelectBox
                                    name="orderType"
                                    id="orderType"
                                    className="form_control"
                                    placeholder={t('Orders.Select_order_type')}
                                    options={classOptions}
                                    onChangeFunc={handleOrderTypeChange(onChange)}
                                    isSearchable={false}
                                    value={value ? classOptions?.filter((val: any) => value === val.value) : null}
                                    required
                                    errorText={error ? true : false}
                                    isShowErrorMsg={false}
                                    isDisabled={true}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {t('Orders.Order_Stepper.Equipment_Type')}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                {memorizedEquipmentTypeSelectBox}
            </div>

            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('Common.Commodity_Type')}</label>
                </div>
                <div className="flex-1 flex flex-col !max-w-[590px]">
                    <Controller
                        name="insuranceCommodity"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => {
                            const selectedOptions =
                                (Array.isArray(value) ? value : [])
                                    .map((val: string) => {
                                        const found = commodityOptions.find((option: any) => option.value === val);

                                        if (found) return found;

                                        if (val && val.trim()) return { value: val, label: val };

                                        return null;
                                    })
                                    .filter(Boolean) || [];

                            const optionsToDisplay = filteredCommodityOptions.length > 0 || commodityInputValue.trim() ? filteredCommodityOptions : commodityOptions;

                            return (
                                <SelectBox
                                    key={`commodity-select-${commoditySelectKey}`}
                                    SelectDropdownWrapClass="multi-select-v2-dropdown"
                                    classComp="!bg-white"
                                    options={optionsToDisplay}
                                    value={selectedOptions}
                                    onChangeFunc={handleInsuranceCommodityMultipleChange(onChange)}
                                    onInputChange={handleCommodityInputChange}
                                    placeholder={t('Common.Select_commodity')}
                                    errorText={isLoadInsuranceEnabled && error?.message ? true : false}
                                    isClearable={true}
                                    isMultipleSelection={true}
                                    closeMenuOnSelect={false}
                                    onClearFunc={handleClearCommodities(onChange)}
                                    isMultipleEquipmentType={true}
                                    maxVisibleValues={8}
                                    keepMenuOpenOnMultiSelect={true}
                                    autoCompleteSelect={true}
                                    onCreateOption={handleCreateCommodityOption(onChange)}
                                    isValidNewOption={Boolean(commodityInputValue && filteredCommodityOptions.length === 0)}
                                    createOptionText="Add new commodity"
                                    isSearchable={true}
                                    forceSearchable={true}
                                    noOptionMessage="No options found"
                                    onMenuOpen={handleCommodityMenuOpen}
                                    onMenuClose={handleCommodityMenuClose}
                                    menuIsOpen={isCommodityMenuOpen}
                                    maintainFocusOnClear={true}
                                    isShowInputValue={true}
                                    defaultValueInput={commodityInputValue}
                                    isHideCheckbox
                                    onKeyDown={handleCommodityKeyDown(onChange, value || [])}
                                />
                            );
                        }}
                    />
                </div>
            </div>

            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex items-center justify-between">
                    <Controller
                        name="isLoadInsuranceEnabled"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Toggle
                                isChecked={value || false}
                                onChange={handleLoadInsuranceToggle(onChange)}
                                label={t('Common.Load_Insurance')}
                                switchClassName="cursor-pointer"
                                name="isLoadInsuranceEnabled"
                                labelPosition="left"
                                id="isLoadInsuranceEnabled"
                                isDisabled={false}
                            />
                        )}
                    />
                </div>
                <div className="stepper-lable-content-wrap flex-1">
                    <div className="flex-wrap flex gap-x-2">
                        <Controller
                            name="insuranceCommercialValue"
                            control={control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <InputText
                                    parentClassName="flex-1"
                                    inputType="number"
                                    value={isLoadInsuranceEnabled ? value : ''}
                                    onChangeFunc={handleInsuranceCommercialValueChange(onChange)}
                                    placeholder="Enter commercial value"
                                    isError={isLoadInsuranceEnabled && !!error}
                                    className="pl-8"
                                    shouldFormatNumber={isLoadInsuranceEnabled}
                                    isAllowDecimal={true}
                                    icon={<CurrencyDollar className="input-currency-sign" />}
                                    disabled={!isLoadInsuranceEnabled}
                                    maxLength={8}
                                />
                            )}
                        />
                        <Controller
                            name="insuranceCurrencyType"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <SelectBox
                                    options={currencyOptions}
                                    value={currencyOptions.find((option) => option.value === value)}
                                    onChangeFunc={handleInsuranceCurrencyChange(onChange)}
                                    parentClassName="min-w-[80px]"
                                    isDisabled={!isLoadInsuranceEnabled}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('Orders.Order_Stepper.Order_Details.PO_Number')}</label>
                </div>
                <div className="flex-1">
                    <Controller
                        name="poNumber"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <InputText inputName="poNumber" placeholder={t('Orders.Order_Stepper.Order_Details.PO_Number_placeholder')} value={value} onChangeFunc={onChange} />
                        )}
                    />
                </div>
            </div>
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('Orders.Order_Stepper.Order_Details.REF_Number')}</label>
                </div>
                <div className="flex-1">
                    <Controller
                        name="refNumber"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <InputText inputName="refNumber" placeholder={t('Orders.Order_Stepper.Order_Details.REF_Number_placeholder')} value={value} onChangeFunc={onChange} />
                        )}
                    />
                </div>
            </div>
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('Orders.Order_Stepper.Order_Details.Pickup_Number')}</label>
                </div>
                <div className="flex-1">
                    <Controller
                        name="pickupNumber"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <InputText inputName="pickupNumber" placeholder={t('Orders.Order_Stepper.Order_Details.Pickup_Number_placeholder')} value={value} onChangeFunc={onChange} />
                        )}
                    />
                </div>
            </div>
            <div className="border-t border-utilityGray200 stepper-content-container border-b pb-5">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('Orders.Order_Stepper.Order_Details.Delivery_Number')}</label>
                </div>
                <div className="flex-1">
                    <Controller
                        name="deliveryNumber"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <InputText inputName="deliveryNumber" placeholder={t('Orders.Order_Stepper.Order_Details.Delivery_Number_placeholder')} value={value} onChangeFunc={onChange} />
                        )}
                    />
                </div>
            </div>
        </>
    );
};

export default OrderDetails;
